import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, collection, getDocs, query, where, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import DOMPurify from 'dompurify';
import './articleDetails.css';
import BlurhashImg from '../../@/components/Image/BlurhashImg';


const ArticleDetails = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const navigate = useNavigate();

  // Apply full-width styles directly to the image wrapper after component mounts
  useEffect(() => {
    const applyFullWidthStyles = () => {
      const imageContainer = document.querySelector('.article-image-container');
      if (imageContainer) {
        // Container styles
        imageContainer.style.width = '100vw';
        imageContainer.style.position = 'relative';
        imageContainer.style.left = '50%';
        imageContainer.style.right = '50%';
        imageContainer.style.marginLeft = '-50vw';
        imageContainer.style.marginRight = '-50vw';
        imageContainer.style.overflow = 'visible'; // Changed from hidden to visible
        
        // LazyLoad wrapper styles
        const lazyWrapper = imageContainer.querySelector('.lazyload-wrapper');
        if (lazyWrapper) {
          lazyWrapper.style.width = '100%';
          lazyWrapper.style.height = 'auto'; // Changed from 100% to auto
          
          // Inner div styles
          const innerDiv = lazyWrapper.querySelector('div');
          if (innerDiv) {
            innerDiv.style.width = '100%';
            innerDiv.style.height = 'auto'; // Changed from 100% to auto
            innerDiv.style.position = 'relative';
          }
          
          // Image styles
          const image = lazyWrapper.querySelector('.lazy-image');
          if (image) {
            image.style.width = '100%';
            image.style.height = 'auto'; // Changed from 100% to auto
            image.style.objectFit = 'contain'; // Changed from cover to contain
            image.style.display = 'block';
            
            // Load event to ensure image displays properly
            image.onload = () => {
              console.log('Image loaded with dimensions:', image.width, 'x', image.height);
            };
          }
        }
      }
    };
    
    // Run after a short delay to ensure DOM is ready
    if (!loading && article) {
      setTimeout(applyFullWidthStyles, 100);
    }
  }, [loading, article]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        console.log('Fetching article with slug or ID:', slug);
        if (!slug) {
          throw new Error('Missing article identifier');
        }

        let docRef = doc(db, 'news', slug);
        let docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          const articlesRef = collection(db, 'news');
          const q = query(articlesRef, where('slug', '==', slug));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            docSnap = querySnapshot.docs[0];
          }
        }

        if (!docSnap.exists()) {
          throw new Error('Article not found');
        }

        const articleData = { 
          id: docSnap.id, 
          ...docSnap.data() 
        };
        
        setArticle(articleData);
        setError(null);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(err.message);
        navigate('/ideas', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, navigate]);

  const createMarkup = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };

  const formatDate = (date) => {
    if (!date) return '';
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(date?.toDate()).toLocaleDateString('en-GB', options);
  };

  if (loading) return <div className="article-details">Loading article...</div>;
  if (error) return <div className="article-details">Error: {error}</div>;
  if (!article) return <div className="article-details">No article found</div>;

  return (
    <div className="article-details">
      <div className="article-header">
        <div className="section__padding">
          <div className="">
            <div className="gpt__blog-container_article">
              <h4>{article.title || 'Untitled Article'}</h4>
            </div>
          </div>
        </div>
        <p className="article-meta">
          <span className="article-date">{formatDate(article.createdAt)}</span>
          {article.category && <span className="article-category"> | {article.category}</span>}
        </p>
      </div>
      
      {article.coverImage && (
        <div className="article-image-container">
          <BlurhashImg 
            src={article.coverImage} 
            alt={article.title || 'Article cover image'}
            className="article-image"
          />
        </div>
      )}

      <div className="section__padding">
        <div className="">
          <div className="gpt__blog-container_article">
            <div 
              className="article-content"
              dangerouslySetInnerHTML={createMarkup(article.content || '<p>No content available</p>')} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetails;